import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, X, Plus } from "lucide-react";
import { VIEWING_OPTIONS, VIEWING_BADGE, toViewingsArray } from "@/lib/fields";
import { api } from "@/lib/api";

export const ViewingsSelect = ({ value, onChange, testid = "viewings-select" }) => {
  const selected = toViewingsArray(value);
  const [custom, setCustom] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState("");

  useEffect(() => { api.getViewings().then(setCustom).catch(() => {}); }, []);

  const options = [...VIEWING_OPTIONS, ...custom.filter((c) => !VIEWING_OPTIONS.includes(c))];
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const addNew = async () => {
    const v = newVal.trim();
    if (!v) return;
    if (!options.includes(v)) {
      try { setCustom(await api.addViewing(v)); } catch { setCustom((c) => [...c, v]); }
    }
    if (!selected.includes(v)) onChange([...selected, v]);
    setNewVal(""); setAdding(false);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="flex min-h-[38px] w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-left text-sm hover:bg-zinc-800/60 focus:outline-none focus:ring-2 focus:ring-[#9B2226]">
          <span className="flex flex-wrap gap-1">
            {selected.length === 0 ? (
              <span className="text-zinc-400">Select viewings…</span>
            ) : (
              selected.map((v) => (
                <span key={v} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-500 ${VIEWING_BADGE}`}>
                  {v}
                  <X className="h-3 w-3 opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); toggle(v); }} />
                </span>
              ))
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 border-zinc-800 bg-zinc-950 p-1" align="start" data-testid={`${testid}-menu`}>
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          {options.map((o) => {
            const on = selected.includes(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testid}-opt-${o}`} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left hover:bg-white/5">
                <span className={`inline-flex rounded px-2 py-0.5 text-sm font-500 ${VIEWING_BADGE}`}>{o}</span>
                {on && <Check className="h-4 w-4 text-[#E0575B]" />}
              </button>
            );
          })}
          <div className="mt-1 border-t border-zinc-800 pt-1">
            {adding ? (
              <div className="flex items-center gap-1 px-1">
                <Input autoFocus value={newVal} onChange={(e) => setNewVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNew(); } }}
                  placeholder="New viewing…" className="h-8 border-zinc-800 bg-zinc-900 text-sm focus-visible:ring-[#9B2226]" data-testid={`${testid}-new-input`} />
                <button type="button" onClick={addNew} className="rounded bg-[#9B2226] px-2 py-1 text-xs text-white hover:bg-[#7A1A1E]" data-testid={`${testid}-new-save`}>Add</button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-[#E0575B] hover:bg-white/5" data-testid={`${testid}-add-new`}>
                <Plus className="h-4 w-4" /> Add new…
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
